/* ============================ SHELL: rail, topbar, auth screens, page router ============================ */
import { useEffect, useState, type FormEvent } from "react";
import {
  DB, api, can, createStore, fromHash, go, hooks, load, nav, railOpen, renormalize, search, setDB, ui, useStore,
} from "./lib/store";
import { connectLive, wsUp } from "./lib/live";
import { MenuRoot, PageBoundary, Toasts, closeMenu, showMenu } from "./ui/common";
import { Modal, ModalRoot } from "./ui/modal";
import { PAGES } from "./pages/registry";
import { Profile, networkPicker } from "./modals/account";

const NAV = [
  { g: "Fleet", items: [
    { id: "home", label: "Overview", ico: "◈" },
    { id: "sites", label: "Sites & edge nodes", ico: "▣" },
    { id: "clusters", label: "Clusters", ico: "⧉" },
    { id: "workloads", label: "Workloads", ico: "▤" },
    { id: "catalog", label: "App catalog", ico: "✦" },
  ] },
  { g: "Automation", items: [
    { id: "images", label: "Images & templates", ico: "◫" },
    { id: "playbooks", label: "Playbooks", ico: "⚙" },
    { id: "jobs", label: "Tasks & jobs", ico: "⟳" },
    { id: "rollouts", label: "Fleet rollouts", ico: "⇥" },
  ] },
  { g: "Operations", items: [
    { id: "monitoring", label: "Monitoring", ico: "◴" },
    { id: "alerts", label: "Alerts", ico: "△" },
    { id: "backups", label: "Backups & restore", ico: "⤓" },
    { id: "recycle", label: "Recycle bin", ico: "♲" },
  ] },
  { g: "Account", items: [
    { id: "org", label: "Organization", ico: "◎" },
    { id: "audit", label: "Audit log", ico: "≡" },
    { id: "settings", label: "Settings", ico: "⚑" },
  ] },
];

export const wCounts = () => ({
  nodes: DB.nodes.length, online: DB.nodes.filter((n: any) => n.status === "online").length,
  offline: DB.nodes.filter((n: any) => n.status === "offline").length, degraded: DB.nodes.filter((n: any) => n.status === "degraded").length,
  vms: DB.workloads.filter((w: any) => w.type === "vm").length, lxc: DB.workloads.filter((w: any) => w.type === "lxc").length,
  ctr: DB.containers.length, jobs: DB.jobs.filter((j: any) => j.status === "running").length,
  alerts: DB.alerts.filter((a: any) => !a.ack && !a.resolved && !a.silenced_until).length,
});

/* ---------- auth state ---------- */
type AuthState = { mode: "boot" | "login" | "invite" | "app"; msg?: string; token?: string };
const auth = createStore<AuthState>({ mode: "boot" });
hooks.signin = (msg?: string) => showSignin(msg);
function showSignin(msg?: string) {
  const inv = location.hash.match(/invite=([\w-]+)/);
  auth.set(inv ? { mode: "invite", token: inv[1] } : { mode: "login", msg: msg || "" });
}
export async function logout() { await api("POST", "/api/auth/logout"); location.hash = ""; location.reload(); }

export async function boot() {
  let d: any;
  try { d = await api("GET", "/api/state", undefined, { noAuthRedirect: true }); }
  catch (e: any) {
    showSignin(["not signed in", "Please sign in", "Unauthorized"].includes(e.message) ? "" : e.message);
    return;
  }
  fromHash();
  if (!ui.expanded.size) d.nodes.forEach((n: any) => ui.expanded.add("n" + n.id));
  setDB(d);
  auth.set({ mode: "app" });
  connectLive();
}

function Login({ msg }: { msg?: string }) {
  const [email, setEmail] = useState(""), [pass, setPass] = useState(""), [code, setCode] = useState("");
  const [needCode, setNeedCode] = useState(false), [err, setErr] = useState(msg || "");
  const submit = async (e: FormEvent) => {
    e.preventDefault(); setErr("");
    try {
      const r = await api("POST", "/api/auth/login", { email, password: pass, code: code || null }, { noAuthRedirect: true });
      if (r.mfa) { setNeedCode(true); setErr("Enter the 6-digit code from your authenticator app."); return; }
      await boot();
      if (r.mfa_setup) Profile.mfa(true);
    } catch (e: any) { setErr(e.message); }
  };
  return (
    <div className="login" id="login">
      <form className="box" onSubmit={submit}>
        <h2>Sign in</h2><p>Edge Compute console</p>
        <label className="f"><span>Email</span><input type="email" autoComplete="username" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} /></label>
        <label className="f"><span>Password</span><input type="password" autoComplete="current-password" required value={pass} onChange={(e) => setPass(e.target.value)} /></label>
        {needCode ? <label className="f"><span>Two-factor code</span><input type="text" inputMode="numeric" autoComplete="one-time-code" autoFocus value={code} onChange={(e) => setCode(e.target.value)} /></label> : null}
        <div className="err">{err}</div>
        <button className="btn primary" style={{ width: "100%", justifyContent: "center" }}>Sign in</button>
      </form>
    </div>
  );
}

function Invite({ token }: { token: string }) {
  const [info, setInfo] = useState<any>(null), [fail, setFail] = useState("");
  const [name, setName] = useState(""), [pass, setPass] = useState(""), [err, setErr] = useState("");
  useEffect(() => { api("GET", "/api/auth/invite/" + token, undefined, { noAuthRedirect: true }).then(setInfo, (e) => setFail(e.message)); }, [token]);
  if (fail) return <div className="login"><div className="box"><h2>Invitation</h2><p>{fail}</p><a href="/" className="btn">Go to sign in</a></div></div>;
  if (!info) return <div className="login"><div className="box"><span className="spin" /></div></div>;
  const accept = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const r = await api("POST", "/api/auth/invite/" + token, { name, password: pass }, { noAuthRedirect: true });
      history.replaceState(null, "", "#/home");
      await boot();
      if (r.mfa_setup) Profile.mfa(true);
    } catch (e: any) { setErr(e.message); }
  };
  return (
    <div className="login">
      <form className="box" onSubmit={accept}>
        <h2>Join {info.org}</h2><p>You were invited as <b>{info.role}</b> ({info.email}).</p>
        <label className="f"><span>Your name</span><input type="text" required value={name} onChange={(e) => setName(e.target.value)} /></label>
        <label className="f"><span>Choose a password (10+ characters)</span><input type="password" autoComplete="new-password" required minLength={10} value={pass} onChange={(e) => setPass(e.target.value)} /></label>
        {info.mfa_required ? <p className="hint">Your organization requires two-factor authentication — you will set it up next.</p> : null}
        <div className="err">{err}</div>
        <button className="btn primary" style={{ width: "100%", justifyContent: "center" }}>Accept invitation</button>
      </form>
    </div>
  );
}

function Rail() {
  const c = wCounts();
  const counts: Record<string, any> = { jobs: c.jobs || "", alerts: c.alerts || "", recycle: DB.recycle.length || "" };
  const open = railOpen.use();
  return (
    <aside className={"rail " + (open ? "open" : "")} id="rail">
      <div className="brand"><div className="mark">{(DB.org.name || "E")[0]}</div><div><b>{DB.org.name || "Edge Compute"}</b><span>{DB.org.org || ""}</span></div></div>
      <button className="orgpick" onClick={() => networkPicker()}>
        <span>{DB.org.network}<small>{DB.org.org || ""}{DB.me.platform_admin ? " · platform admin" : ""}</small></span><span>⌄</span>
      </button>
      {NAV.map((g) => (
        <div key={g.g}>
          <div className="navgroup">{g.g}</div>
          <ul className="nav">
            {g.items.map((i) => (
              <li key={i.id}>
                <button className={nav.route === i.id ? "active" : ""} onClick={() => go(i.id)}>
                  <span className="ico">{i.ico}</span>{i.label}
                  {counts[i.id] !== undefined && counts[i.id] !== "" ? <span className={"count " + (i.id === "alerts" ? "bad" : "")}>{counts[i.id]}</span> : null}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
      <div className="railfoot">Signed in as {DB.me.email} ({DB.me.role})<br />Console {DB.meta.console_url || ""} · agents {DB.meta.public_url || ""}</div>
    </aside>
  );
}

function profileMenu(e: React.MouseEvent) {
  showMenu(e, <>
    <div className="who"><b>{DB.me.name}</b>{DB.me.email} · {DB.me.role}</div>
    <button onClick={() => Profile.edit()}>Profile and password…</button>
    <button onClick={() => Profile.mfa()}>Two-factor authentication{DB.me.mfa ? " (on)" : ""}…</button>
    <hr /><button onClick={() => logout()}>Sign out</button>
  </>);
}

function Topbar({ crumb }: { crumb: string }) {
  const c = wCounts();
  const up = wsUp.use();
  const [q, setQ] = useState("");
  return (
    <header className="topbar">
      <button className="btn ghost sm menu-toggle" onClick={() => railOpen.set((o) => !o)}>☰</button>
      <h1 id="crumb">{crumb}</h1>
      <div className="search"><span className="ico">⌕</span>
        <input type="text" id="gsearch" placeholder="Search nodes, VMs, containers, jobs…" value={q}
          onChange={(e) => { setQ(e.target.value); search(e.target.value); }} />
      </div>
      <button className="awx" onClick={() => go("jobs")}>
        <span className="dot" id="wsdot" style={{ color: up ? "var(--ok)" : "var(--bad)" }} /> <span id="runner">Ansible {DB.meta.ansible || ""}</span>{" "}
        <span style={{ color: "var(--ink-3)" }}>· <span id="jobcount">{c.jobs}</span> running</span>
      </button>
      <button className="btn ghost sm" title="Alerts" onClick={() => go("alerts")}>
        🔔 {c.alerts ? <span id="alertdot" className="badge bad" style={{ display: "inline-flex" }}>{c.alerts}</span> : null}
      </button>
      <div className="avatar" id="avatar" title={`${DB.me.name} — ${DB.me.role}`} onClick={profileMenu}>
        {(DB.me.name || DB.me.email || "?").trim()[0].toUpperCase()}
      </div>
    </header>
  );
}

function Shell() {
  useStore();
  const def = PAGES[nav.route] || PAGES.home;
  let crumb = "Edge Compute";
  try { crumb = def.crumb(nav.param, nav.tab) || crumb; } catch { /* page draws its own error */ }
  useEffect(() => { document.title = (crumb ? crumb + " · " : "") + (DB.org.name || "Edge Compute"); }, [crumb]);
  const C = def.C;
  return (
    <div className="shell" id="shell">
      <Rail />
      <div className="main">
        <Topbar crumb={crumb} />
        <div className="view" id="view">
          <div className="wrap">
            <PageBoundary key={nav.route + "/" + nav.param + "/" + nav.tab} onHome={() => go("home")}>
              <C param={nav.param} tab={nav.tab} />
            </PageBoundary>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const a = auth.use();
  useEffect(() => {
    boot();
    const pop = () => { fromHash(); closeMenu(); renormalize(); };
    window.addEventListener("popstate", pop);
    // relative times ("3m ago") refresh every 15 s; a full state re-read every 30 s is the safety net for missed events
    const t1 = setInterval(() => { if (auth.get().mode === "app" && !Modal.isOpen()) renormalize(); }, 15000);
    const t2 = setInterval(() => { if (auth.get().mode === "app") load().catch(() => {}); }, 30000);
    return () => { window.removeEventListener("popstate", pop); clearInterval(t1); clearInterval(t2); };
  }, []);
  return (
    <>
      {a.mode === "login" ? <Login key={a.msg} msg={a.msg} /> : null}
      {a.mode === "invite" && a.token ? <Invite token={a.token} /> : null}
      {a.mode === "app" && DB.me && DB.me.id ? <Shell /> : null}
      <ModalRoot />
      <MenuRoot />
      <Toasts />
    </>
  );
}
export { can };
